import { nextTick, onUnmounted, watch } from 'vue'

type Invoker =
  { readonly kind: 'captured'; readonly element: HTMLElement } | { readonly kind: 'none' }
type InvokerPanel =
  { readonly kind: 'inside'; readonly element: Element } | { readonly kind: 'outside' }

const NO_INVOKER: Invoker = { kind: 'none' }
const OUTSIDE_PANEL: InvokerPanel = { kind: 'outside' }

/** Returns focus to the control that opened an overlay once it closes, unless another overlay now holds focus. */
export const useOverlayFocusRestore = (isOpen: () => boolean): { restore: () => void } => {
  if (typeof document === 'undefined') return { restore: () => {} }

  let invoker: Invoker = NO_INVOKER
  let invokerPanel: InvokerPanel = OUTSIDE_PANEL

  watch(
    () => isOpen(),
    (open) => {
      if (!open) return
      const active = document.activeElement
      invoker =
        active instanceof HTMLElement && active !== document.body
          ? { kind: 'captured', element: active }
          : NO_INVOKER
      void nextTick(() => {
        const panel = document.activeElement?.closest('[role="dialog"]')
        invokerPanel = panel ? { kind: 'inside', element: panel } : OUTSIDE_PANEL
      })
    },
    { immediate: true }
  )

  const holdsNoOtherOverlay = (): boolean => {
    const active = document.activeElement
    if (active === null || active === document.body) return true
    const holder = active.closest('[role="dialog"]')
    return holder === null || (invokerPanel.kind === 'inside' && holder === invokerPanel.element)
  }

  const restore = (): void => {
    const target = invoker
    invoker = NO_INVOKER
    invokerPanel = OUTSIDE_PANEL
    if (target.kind === 'captured' && target.element.isConnected && holdsNoOtherOverlay()) {
      target.element.focus()
    }
  }

  onUnmounted(restore)

  return { restore }
}
